import { useEffect, useMemo, useState } from "react";
import { Autocomplete } from "@/components/Autocomplete";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { getRecentAuftraege, searchAuftraege, type AuftragLookup } from "@/lib/material";
import type { Order } from "@/lib/projects";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";
import { toast } from "@/lib/toast";
import {
  defaultMoveHours,
  defaultSealReference,
  executeProjectKorrektur,
  previewProjectKorrektur,
  PROJECT_KORREKTUR_FALLTYP_HINTS,
  PROJECT_KORREKTUR_FALLTYP_LABELS,
  PROJECT_KORREKTUR_FALLTYPEN,
  type ProjectKorrekturFalltyp,
  type ProjectKorrekturInventory,
  type ProjectKorrekturPreview,
} from "@/lib/projectKorrektur";

function inventorySummary(inv: ProjectKorrekturInventory): string {
  const bits = [
    `${inv.timesheet_count} SN`,
    `${inv.employee_time_count} App-Stunden`,
    `${inv.order_count} Bestellungen`,
    `${inv.gd_count} Belege`,
  ];
  if (inv.material_anforderungen_count) {
    bits.push(`${inv.material_anforderungen_count} MA`);
  }
  return bits.join(" · ");
}

export function ProjectKorrekturDialog({
  open,
  project,
  onClose,
  onSaved,
}: {
  open: boolean;
  project: Order;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const [other, setOther] = useState<AuftragLookup | null>(null);
  const [preview, setPreview] = useState<ProjectKorrekturPreview | null>(null);
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [falltyp, setFalltyp] = useState<ProjectKorrekturFalltyp>("verteilte_akte");
  const [leadingId, setLeadingId] = useState<number>(project.id);
  const [moveHours, setMoveHours] = useState(true);
  const [sealReference, setSealReference] = useState(true);
  const [begruendung, setBegruendung] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOther(null);
    setPreview(null);
    setPreviewError("");
    setFalltyp("verteilte_akte");
    setLeadingId(project.id);
    setMoveHours(defaultMoveHours("verteilte_akte"));
    setSealReference(defaultSealReference("verteilte_akte"));
    setBegruendung("");
  }, [open, project.id]);

  useEffect(() => {
    if (!open || !other?.id || other.id === project.id) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError("");
    void previewProjectKorrektur(project.id, other.id)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setLeadingId(data.recommended_leading_id);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPreview(null);
        setPreviewError(err instanceof Error ? err.message : "Vorschau konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, other?.id, project.id]);

  useEffect(() => {
    const defaults = preview?.defaults?.[falltyp];
    setMoveHours(defaults?.move_hours ?? defaultMoveHours(falltyp));
    setSealReference(defaults?.seal_reference ?? defaultSealReference(falltyp));
  }, [falltyp, preview]);

  const left = preview?.left;
  const right = preview?.right;
  const referenceId = leadingId === project.id ? other?.id : project.id;
  const leadingInv = leadingId === left?.auftrag_id ? left : right;
  const referenceInv = referenceId === left?.auftrag_id ? left : right;

  const canSubmit = useMemo(() => {
    return Boolean(
      other?.id &&
        other.id !== project.id &&
        preview &&
        begruendung.trim() &&
        !busy &&
        !previewLoading,
    );
  }, [other?.id, project.id, preview, begruendung, busy, previewLoading]);

  const handleSubmit = async () => {
    if (!other?.id || !canSubmit) return;
    setBusy(true);
    try {
      const result = await executeProjectKorrektur(project.id, {
        other_auftrag_id: other.id,
        leading_auftrag_id: leadingId,
        falltyp,
        begruendung: begruendung.trim(),
        move_hours: falltyp === "doppelbuchung" ? false : moveHours,
        seal_reference: sealReference,
      });
      toast.success(
        `Zusammengeführt: führend ${result.leading_auftragsnummer}, Verweisakte ${result.reference_auftragsnummer}.`,
      );
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Zusammenführung ist fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? () => {} : onClose}
      dirty={Boolean(other || begruendung.trim())}
      size="xl"
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      plainBackdrop
      title="Projekte zusammenführen"
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={busy} />
          <Button type="button" disabled={!canSubmit} loading={busy} onClick={() => void handleSubmit()}>
            Korrektur ausführen
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-label-secondary">
          Nichts löschen. Eine Akte bleibt führend, die andere wird zur auffindbaren Verweisakte.
          Unterschriebene Stunden, Bestellungen und Rechnungen werden nicht umgeschrieben.
        </p>

        <Autocomplete<AuftragLookup>
          key={`${project.id}-${open ? "open" : "closed"}`}
          label="Anderes Projekt"
          placeholder="Nummer, Kunde oder Ort …"
          minChars={0}
          loadOptions={async (q) => {
            const list = q.trim()
              ? await searchAuftraege(q)
              : await getRecentAuftraege({ limit: 15 });
            return list
              .filter((row) => row.id !== project.id)
              .map((row) => ({
                key: String(row.id),
                label: formatAuftragLookupLabel(row),
                description: row.beschreibung || undefined,
                value: row,
              }));
          }}
          onSelect={(opt) => setOther(opt?.value ?? null)}
        />

        {previewLoading ? (
          <p className="text-sm text-label-tertiary">Bestand beider Projekte wird gelesen …</p>
        ) : null}
        {previewError ? <Alert variant="error">{previewError}</Alert> : null}

        {preview && left && right ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {[left, right].map((inv) => (
              <div
                key={inv.auftrag_id}
                className="rounded-ios-lg border border-separator bg-fill-tertiary/40 px-3 py-2 text-sm"
              >
                <p className="font-semibold text-label">{inv.auftragsnummer}</p>
                <p className="text-label-secondary">{inv.kunde || "—"}</p>
                <p className="mt-1 text-xs text-label-tertiary">{inventorySummary(inv)}</p>
                {inv.timesheets_locked.length ? (
                  <p className="mt-1 text-xs text-status-orange">
                    Gesperrte SN: {inv.timesheets_locked.map((row) => row.label).join(", ")}
                  </p>
                ) : null}
                {inv.orders.length ? (
                  <p className="mt-1 text-xs text-label-tertiary">
                    Bestellungen bleiben liegen: {inv.orders.map((row) => row.label).join(", ")}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <fieldset className="space-y-2">
          <legend className="eh-label">Falltyp</legend>
          {PROJECT_KORREKTUR_FALLTYPEN.map((key) => (
            <label key={key} className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="radio"
                name="korrektur-falltyp"
                className="mt-1"
                checked={falltyp === key}
                onChange={() => setFalltyp(key)}
              />
              <span>
                <span className="font-medium text-label">{PROJECT_KORREKTUR_FALLTYP_LABELS[key]}</span>
                <span className="block text-label-secondary">{PROJECT_KORREKTUR_FALLTYP_HINTS[key]}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {preview && left && right ? (
          <fieldset className="space-y-2">
            <legend className="eh-label">Führendes Projekt</legend>
            {[left, right].map((inv) => (
              <label key={inv.auftrag_id} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="korrektur-leading"
                  checked={leadingId === inv.auftrag_id}
                  onChange={() => setLeadingId(inv.auftrag_id)}
                />
                <span>
                  {inv.auftragsnummer}
                  {preview.recommended_leading_id === inv.auftrag_id ? " (Empfehlung)" : ""}
                </span>
              </label>
            ))}
          </fieldset>
        ) : null}

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={falltyp === "doppelbuchung" ? false : moveHours}
            disabled={falltyp === "doppelbuchung"}
            onChange={(e) => setMoveHours(e.target.checked)}
          />
          <span>
            Unsignierte Stunden von der Verweisakte auf die führende Akte legen.
            {falltyp === "doppelbuchung"
              ? " Bei Doppelbuchung bleibt das aus, sonst würden sich Stunden verdoppeln."
              : ""}
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={sealReference}
            onChange={(e) => setSealReference(e.target.checked)}
          />
          <span>
            Verweisakte stilllegen (ablehnen wenn möglich, sonst Buchungssperre). Die Nummer bleibt
            suchbar.
          </span>
        </label>

        {referenceInv?.can_ablehnen === false && sealReference ? (
          <Alert variant="info">
            {referenceInv.auftragsnummer} ist nicht mehr „offen“ oder schon beauftragt — Ablehnen
            geht nicht. Es wird eine Buchungssperre gesetzt.
          </Alert>
        ) : null}

        {leadingInv && referenceInv && falltyp === "falsches_material" ? (
          <Alert variant="info">
            Bestellungen an {referenceInv.auftragsnummer} werden nicht umgehängt. Sie stehen im
            Protokoll beider Akten, die Papierlage bleibt die Wahrheit nach außen.
          </Alert>
        ) : null}

        <div className="space-y-1">
          <label className="eh-label" htmlFor="korrektur-grund">
            Begründung<span className="ml-1 text-status-red">*</span>
          </label>
          <textarea
            id="korrektur-grund"
            className="eh-input min-h-[5rem]"
            rows={4}
            placeholder="Warum diese Akte führt, und was bewusst liegen bleibt …"
            value={begruendung}
            disabled={busy}
            onChange={(e) => setBegruendung(e.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}
